import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const initialForm = {
  full_name: "",
  email: "",
  phone: "",
  project_location: "",
  project_type: "",
  message: "",
};

export default function InquiryForm() {
  const [formData, setFormData] = useState(initialForm);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((current) => ({ ...current, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${BACKEND_URL}/api/inquiries`, formData);
      setSubmitted(true);
      setFormData(initialForm);
      toast.success("Thanks — your inquiry was sent successfully.");
    } catch (error) {
      console.error("Unable to submit inquiry", error);
      toast.error("We couldn't send your message. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form
      className="space-y-5"
      data-testid="contact-inquiry-form"
      onSubmit={handleSubmit}
    >
      <div className="grid gap-5 md:grid-cols-2">
        <Input
          className="h-12 rounded-sm border-border/90 bg-white/80 text-sm"
          data-testid="contact-full-name-input"
          name="full_name"
          onChange={handleChange}
          placeholder="Full name"
          required
          value={formData.full_name}
        />
        <Input
          className="h-12 rounded-sm border-border/90 bg-white/80 text-sm"
          data-testid="contact-email-input"
          name="email"
          onChange={handleChange}
          placeholder="Email address"
          required
          type="email"
          value={formData.email}
        />
      </div>

      <div className="grid gap-5 md:grid-cols-2">
        <Input
          className="h-12 rounded-sm border-border/90 bg-white/80 text-sm"
          data-testid="contact-phone-input"
          name="phone"
          onChange={handleChange}
          placeholder="Phone"
          required
          value={formData.phone}
        />
        <Input
          className="h-12 rounded-sm border-border/90 bg-white/80 text-sm"
          data-testid="contact-location-input"
          name="project_location"
          onChange={handleChange}
          placeholder="Project location"
          required
          value={formData.project_location}
        />
      </div>

      <Input
        className="h-12 rounded-sm border-border/90 bg-white/80 text-sm"
        data-testid="contact-project-type-input"
        name="project_type"
        onChange={handleChange}
        placeholder="Project type (new build, full renovation, beach home, etc.)"
        required
        value={formData.project_type}
      />

      <Textarea
        className="min-h-[160px] rounded-sm border-border/90 bg-white/80 text-sm"
        data-testid="contact-message-input"
        name="message"
        onChange={handleChange}
        placeholder="Tell us about your lot, timing, goals, and the kind of home experience you want to create."
        required
        value={formData.message}
      />

      <div className="flex flex-col items-start gap-3 md:flex-row md:items-center md:justify-between">
        <Button
          className="h-12 rounded-sm bg-primary px-8 text-primary-foreground hover:bg-[#1C3359]"
          data-testid="contact-form-submit-button"
          disabled={isSubmitting}
          type="submit"
        >
          {isSubmitting ? "Sending..." : "Request Consultation"}
        </Button>

        <p
          className="text-sm text-muted-foreground"
          data-testid="contact-form-response-text"
        >
          {submitted
            ? "Message received — Suncoast Homes will follow up personally."
            : "We review each inquiry personally and respond with discretion."}
        </p>
      </div>
    </form>
  );
}